import { currencyFractionDigits } from '../../../../shared/utils/commercial-money';
import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, Optional } from '@angular/core';
import { PermissionService } from '../../../../security/permission.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../../shared/page-layout/page-data-panel.component';
import { PageActionPaneComponent } from '../../../../shared/page-layout/page-action-pane.component';
import { FinancePdfViewButtonComponent } from '../../../finance-pdf-view-button.component';
import { AccountsService, FinancePdfStatus } from '../../../accounts.service';
import { Invoice } from '../../../invoice/invoice.model';
import { AccountingDocumentAction, DocumentAuditEvent, GlPreview } from '../../../accounting-lifecycle.model';
import { AccountingTabLabelComponent } from '../../../shared/accounting-tab-label.component';
import { AccountingWorkflowActionsComponent } from '../../../shared/accounting-workflow-actions.component';
import { AccountingAuditTimelineComponent } from '../../../shared/accounting-audit-timeline.component';
import { AccountingGlPreviewPanelComponent } from '../../../shared/accounting-gl-preview-panel.component';
import { AccountingAttachmentsPanelComponent } from '../../../shared/accounting-attachments-panel.component';
import { ManualSourceBadgeComponent } from '../../../shared/manual-source-badge.component';
import { AccountingTaxBreakdownPanelComponent } from '../../../shared/accounting-tax-breakdown-panel.component';
import { ZatcaCompliancePanelComponent } from '../../../shared/zatca-compliance-panel.component';
import { AccountingComplianceEvent } from '../../../accounting-compliance.model';
import { CrmPartyContextService } from '../../../../shared/crm-client-contact-picker/crm-party-context.service';
import { CrmClientCommunication } from '../../../../crm/crm.model';
import { CommercialContextDialogComponent } from '../../../shared/commercial-context-dialog.component';
import { PendingActionTracker } from '../../../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-invoice-detail',
    standalone: true,
    imports: [CommercialContextDialogComponent, FinancePdfViewButtonComponent, CommonModule, FormsModule, ButtonModule, TableModule, TabsModule, TagModule, ToastModule, TooltipModule, DialogModule, InputTextModule, TextareaModule, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent, PageActionPaneComponent, AccountingTabLabelComponent, AccountingWorkflowActionsComponent, AccountingAuditTimelineComponent, AccountingGlPreviewPanelComponent, AccountingAttachmentsPanelComponent, ManualSourceBadgeComponent, AccountingTaxBreakdownPanelComponent, ZatcaCompliancePanelComponent],
    providers: [MessageService],
    template: `
        <app-page-shell>
            <p-toast position="top-center"></p-toast>
            <app-page-detail-hero [auditStamp]="invoice" eyebrow="Customer Invoice" [title]="invoice?.invoiceNumber || 'Invoice'"
                [subtitle]="invoice ? crmPartyContext.displayName(invoice) : 'Receivable document command center'" icon="pi pi-file">
                <div tags>
                    <p-tag [value]="invoice?.documentStatus || 'UNKNOWN'" [severity]="statusSeverity(invoice?.documentStatus)"></p-tag>
                    <p-tag [value]="invoice?.postingStatus || 'NOT_POSTED'" severity="info"></p-tag>
                    <p-tag [value]="'Open ' + formatMoney(invoice?.openAmount, invoice?.country?.currencyCode)" severity="warn"></p-tag>
                    <p-tag [value]="'ZATCA ' + (invoice?.zatcaStatus || 'N/A')" severity="secondary"></p-tag>
                </div>
            </app-page-detail-hero>

            <div class="document-detail-layout" *ngIf="invoice">
                <div class="document-detail-content">
            <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
                <p-tablist>
                    <p-tab value="overview"><app-accounting-tab-label icon="pi pi-info-circle" label="Overview"></app-accounting-tab-label></p-tab>
                    <p-tab value="lines"><app-accounting-tab-label icon="pi pi-list" label="Lines"></app-accounting-tab-label></p-tab>
                    <p-tab value="receipts"><app-accounting-tab-label icon="pi pi-receipt" label="Receipts"></app-accounting-tab-label></p-tab>
                    <p-tab value="debitnotes"><app-accounting-tab-label icon="pi pi-plus-circle" label="Debit Notes"></app-accounting-tab-label></p-tab>
                    <p-tab value="journal"><app-accounting-tab-label icon="pi pi-book" label="Journal"></app-accounting-tab-label></p-tab>
                    <p-tab value="attachments"><app-accounting-tab-label icon="pi pi-paperclip" label="Attachments"></app-accounting-tab-label></p-tab>
                    <p-tab value="compliance"><app-accounting-tab-label icon="pi pi-shield" label="Compliance"></app-accounting-tab-label></p-tab>
                    <p-tab value="workflow"><app-accounting-tab-label icon="pi pi-sitemap" label="Workflow"></app-accounting-tab-label></p-tab>
                    <p-tab value="activity"><app-accounting-tab-label icon="pi pi-history" label="Activity"></app-accounting-tab-label></p-tab>
                    <p-tab value="related"><app-accounting-tab-label icon="pi pi-share-alt" label="Related"></app-accounting-tab-label></p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="overview">
                        <app-page-data-panel title="Invoice Summary">
                            <div class="detail-grid">
                                <div><strong>Total</strong><span>{{ formatMoney(invoice.totalAmount, invoice.country.currencyCode) }}</span></div>
                                <div><strong>Tax</strong><span>{{ formatMoney(invoice.totalTax, invoice.country.currencyCode) }}</span></div>
                                <div><strong>Open</strong><span>{{ formatMoney(invoice.openAmount, invoice.country.currencyCode) }}</span></div>
                                <div><strong>Due date</strong><span>{{ invoice.dueDate | date:'yyyy-MM-dd' }}</span></div>
                                <div><strong>Terms</strong><span>{{ invoice.paymentTerms || '-' }}</span></div>
                                <div><strong>Collection</strong><span>{{ invoice.collectionStatus || '-' }}</span></div>
                                <div><strong>External reference</strong><span>{{ invoice.externalReferenceNumber || invoice.sourceReference || '-' }}</span></div>
                                <div><strong>Issued by</strong><span>{{ invoice.issuerName || '-' }}</span></div>
                                <div><strong>Issuer contact</strong><span>{{ invoice.issuerEmail || invoice.issuerPhoneNumber || '-' }}</span></div>
                            </div>
                        </app-page-data-panel>

                    </p-tabpanel>
                    <p-tabpanel value="lines">
                        <app-page-data-panel title="Lines" [resultText]="invoice.invoiceLines.length + ' lines'">
                            <p-table [value]="invoice.invoiceLines || []">
                                <ng-template pTemplate="header"><tr><th>Description</th><th>Arabic Description</th><th>Invoice Comment</th><th>Source</th><th>Qty</th><th>Amount</th><th>VAT</th></tr></ng-template>
                                <ng-template pTemplate="body" let-line><tr><td>{{ line.itemDescription || line.itemNumber || '-' }}</td><td dir="rtl">{{ line.itemDescriptionLocal || '-' }}</td><td>{{ line.invoiceComment || '-' }}</td><td>{{ compactReference(line.detailReferenceNumber || line.headerReferenceNumber) }}</td><td>{{ line.quantity }}</td><td>{{ formatMoney(line.chargeAmountInclTax, invoice.country.currencyCode) }}</td><td>{{ formatMoney(line.taxAmount, invoice.country.currencyCode) }}</td></tr></ng-template>
                            </p-table>
                        </app-page-data-panel>
                    </p-tabpanel>
                    <p-tabpanel value="receipts">
                        <app-page-data-panel title="Receipts" [resultText]="invoice.receipts.length + ' receipts'">
                            <p-table [value]="invoice.receipts || []">
                                <ng-template pTemplate="header"><tr><th>Receipt</th><th>Amount</th><th>Status</th></tr></ng-template>
                                <ng-template pTemplate="body" let-receipt><tr><td>{{ receipt.receiptNumber }}</td><td>{{ formatMoney(receipt.allocatedAmount || receipt.amount, invoice.country.currencyCode) }}</td><td>{{ receipt.allocationStatus }}</td></tr></ng-template>
                            </p-table>
                        </app-page-data-panel>
                    </p-tabpanel>
                    <p-tabpanel value="debitnotes">
                        <app-page-data-panel title="Debit Notes" [resultText]="(($any(invoice).debitNotes || []).length || 0) + ' notes'">
                            <p-table [value]="$any(invoice).debitNotes || []">
                                <ng-template pTemplate="header"><tr><th>Debit note</th><th>Amount</th><th>Status</th></tr></ng-template>
                                <ng-template pTemplate="body" let-note><tr><td>{{ note.debitNoteNumber }}</td><td>{{ formatMoney(note.debitNoteAmount, invoice.country.currencyCode) }}</td><td>{{ note.allocationStatus || note.documentStatus }}</td></tr></ng-template>
                            </p-table>
                        </app-page-data-panel>
                    </p-tabpanel>
                    <p-tabpanel value="journal">
                        <app-accounting-gl-preview-panel [preview]="glPreview"></app-accounting-gl-preview-panel>
                    </p-tabpanel>
                    <p-tabpanel value="attachments">
                        <app-accounting-attachments-panel documentType="INVOICE" [documentId]="invoice.invoiceId" [attachments]="$any(invoice).attachments || []"></app-accounting-attachments-panel>
                    </p-tabpanel>
                    <p-tabpanel value="compliance">
                        <app-zatca-compliance-panel [status]="invoice.zatcaStatus" [invoiceHash]="invoice.invoiceHash"
                            [qrCodeValue]="invoice.qrCodeValue" [errorMessage]="invoice.zatcaErrorMessage"
                            [events]="complianceEvents" [retrying]="retryingZatca" (retry)="retryZatca()"></app-zatca-compliance-panel>
                        <app-accounting-tax-breakdown-panel [lines]="invoice.invoiceLines || []"></app-accounting-tax-breakdown-panel>
                    </p-tabpanel>
                    <p-tabpanel value="workflow">
                        <app-page-data-panel title="Workflow State">
                            <div class="detail-grid">
                                <div><strong>Document</strong><span>{{ invoice.documentStatus || '-' }}</span></div>
                                <div><strong>Workflow</strong><span>{{ invoice.workflowStatus || '-' }}</span></div>
                                <div><strong>Posting</strong><span>{{ invoice.postingStatus || '-' }}</span></div>
                                <div><strong>Approved</strong><span>{{ invoice.approvedAt | date:'short' }}</span></div>
                                <div><strong>Posted</strong><span>{{ invoice.postedAt | date:'short' }}</span></div>
                                <div><strong>Reversal journal</strong><span>{{ invoice.reversalJournalEntryId || '-' }}</span></div>
                            </div>
                        </app-page-data-panel>
                    </p-tabpanel>
                    <p-tabpanel value="activity">
                        <app-accounting-audit-timeline [events]="auditEvents"></app-accounting-audit-timeline>
                    </p-tabpanel>
                    <p-tabpanel value="related">
                        <app-page-data-panel title="Related">
                            <div class="detail-grid">
                                <div><strong>Source</strong><span><app-manual-source-badge [sourceType]="invoice.sourceType || 'SYSTEM'" [sourceReference]="invoice.sourceReference" [manualReasonCode]="invoice.manualReasonCode"></app-manual-source-badge></span></div>
                                <div><strong>Delivery</strong><span>{{ invoice.deliveryReference || '-' }}</span></div>
                                <div><strong>Manual reason</strong><span>{{ invoice.manualReasonCode || '-' }}</span></div>
                            </div>
                        </app-page-data-panel>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>
                </div>
                <app-page-action-pane title="Invoice Actions" subtitle="Manage the document lifecycle, delivery, and compliance.">
                    <div class="action-section">
                        <span class="action-section-title">Navigation</span>
                        <div class="action-buttons">
                            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="back()"></p-button>
                            <p-button label="Edit" icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="editInvoice()" *ngIf="canEditInvoice()"></p-button>
                            <p-button label="Edit commercial details" icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="openCommercialDetails()" *ngIf="canEditCommercialDetails()"></p-button>
                        </div>
                    </div>
                    <div class="action-section action-section--primary">
                        <span class="action-section-title">Workflow</span>
                        <p *ngIf="invoiceWasPosted" class="text-sm">Correct a posted invoice through a linked credit or debit note.</p>
                        <app-accounting-workflow-actions documentType="INVOICE" [documentStatus]="invoice.documentStatus" [postingStatus]="invoice.postingStatus" [postedAt]="invoice.postedAt"
                            [loadingAction]="workflowLoadingAction" [disabled]="markingSent || retryingZatca || generatingReceipt"
                            (workflow)="workflow($event.action, $event.reason)" (post)="post()"></app-accounting-workflow-actions>
                    </div>
                    <div class="action-section">
                        <span class="action-section-title">Send & Compliance</span>
                        <div class="action-buttons">
                            <p-button [label]="invoice.sentStatus === 'SENT' || invoice.sentStatus === 'RESENT' ? 'Resend' : 'Send'" icon="pi pi-envelope" severity="secondary" [outlined]="true"
                                [loading]="markingSent" [disabled]="isActionBlocked(markingSent)" (click)="markSent()" *ngIf="invoice.postingStatus === 'POSTED'"></p-button>
                            <p-button label="QR" icon="pi pi-qrcode" severity="secondary" [outlined]="true" (click)="showQr()" *ngIf="invoice.qrCodeValue"></p-button>
                            <p-button label="Retry ZATCA" icon="pi pi-refresh" severity="warn" [outlined]="true"
                                [loading]="retryingZatca" [disabled]="isActionBlocked(retryingZatca)" (click)="retryZatca()" *ngIf="invoice.zatcaStatus === 'FAILED'"></p-button>
                        </div>
                    </div>
                    <div class="action-section">
                        <span class="action-section-title">Documents</span>
                        <div class="action-buttons">
                            <p-button label="XML" icon="pi pi-code" severity="secondary" [outlined]="true" (click)="downloadXml()"></p-button>
                            <app-finance-pdf-view-button kind="invoice" [documentId]="invoice.invoiceId" [documentReference]="invoice.invoiceNumber" [postingStatus]="invoice.postingStatus" [disabled]="!pdfStatus || pdfStatus.status === 'MISSING' || documentActions.isPending('download')"></app-finance-pdf-view-button>
                            <p-button [label]="pdfStatus?.status === 'ORIGINAL' ? 'Download original PDF' : pdfStatus?.status === 'LEGACY' ? 'Download legacy stored PDF' : pdfStatus?.status === 'MISSING' ? 'Original unavailable' : pdfStatus?.status === 'DRAFT' ? 'Preview draft PDF' : 'Checking PDF'"
                                icon="pi pi-download" severity="info" [outlined]="true"
                                [loading]="documentActions.isPending('download')" [disabled]="!pdfStatus || documentActions.isPending('reprint')"
                                (click)="download()"></p-button>
                            <app-finance-pdf-view-button *ngIf="pdfStatus?.reprintAvailable" kind="invoice" [documentId]="invoice.invoiceId" [documentReference]="invoice.invoiceNumber" [postingStatus]="invoice.postingStatus" [reprint]="true" [disabled]="documentActions.isPending('reprint')" (conflict)="load()"></app-finance-pdf-view-button>
                            <p-button *ngIf="pdfStatus?.reprintAvailable" label="Reprint PDF" icon="pi pi-print" severity="secondary" [outlined]="true"
                                [loading]="documentActions.isPending('reprint')" [disabled]="documentActions.isPending('download')" (click)="reprint()"></p-button>
                            <p-button label="Generate Receipt" icon="pi pi-receipt" severity="help" [outlined]="true"
                                [loading]="generatingReceipt" [disabled]="isActionBlocked(generatingReceipt)" (click)="generateReceipt()" *ngIf="invoice.postingStatus === 'POSTED' && invoice.status !== 'PAID'"></p-button>
                            <p-button label="Create Customer Debit Note" icon="pi pi-plus-circle" severity="secondary" [outlined]="true" (click)="createDebitNote()" *ngIf="invoice.postingStatus === 'POSTED' && permissionService?.getPermissionCodes()?.has('accounts.debit-note.create')"></p-button>
                            <p-button label="Create Customer Credit Note" icon="pi pi-minus-circle" severity="secondary" [outlined]="true" (click)="createCustomerCreditNote()" *ngIf="invoice.postingStatus === 'POSTED' && permissionService?.getPermissionCodes()?.has('accounts.customer-credit-note.create')"></p-button>
                        </div>
                    </div>
                </app-page-action-pane>
            </div>
            <p-dialog header="Invoice QR Code Payload" [(visible)]="qrDialogVisible" [modal]="true" [style]="{ width: '42rem' }">
                <pre class="qr-payload">{{ invoice?.qrCodeValue }}</pre>
                <ng-template pTemplate="footer">
                    <p-button label="Copy" icon="pi pi-copy" (click)="copyQr()"></p-button>
                </ng-template>
            </p-dialog>
            <app-commercial-context-dialog [(visible)]="commercialDialogVisible"
                [documentNumber]="invoice?.invoiceNumber || ''" [partyName]="invoice ? crmPartyContext.displayName(invoice) : ''"
                [companyId]="invoice?.owningCompany?.id" [crmClientId]="invoice?.crmClientId"
                [(externalReference)]="commercialExternalReference" [(dueDate)]="commercialDueDate"
                [(projectId)]="commercialProjectId" [(contactId)]="commercialContactId"
                [contacts]="commercialContacts" [contactsLoading]="commercialContactsLoading"
                [lineComments]="commercialLineComments" [saving]="commercialSaving" [error]="commercialError"
                (save)="saveCommercialDetails()"></app-commercial-context-dialog>
        </app-page-shell>
    `,
    styles: [`
        .document-detail-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(20rem, 22rem); gap: 1rem; align-items: start; }
        .document-detail-content { min-width: 0; }

        .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit,minmax(180px,1fr));
            gap: .75rem;
        }

        .detail-grid div {
            display: flex;
            flex-direction: column;
            gap: .25rem;
            padding: .75rem;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
        }

        .detail-grid span {
            color: #475569;
        }

        .qr-payload {
            white-space: pre-wrap;
            word-break: break-word;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 1rem;
            max-height: 22rem;
            overflow: auto;
        }

        @media (max-width: 1080px) { .document-detail-layout { grid-template-columns: 1fr; } }
    `]
})
export class InvoiceDetailComponent implements OnInit {
    readonly documentActions = new PendingActionTracker<string>();
    invoice?: Invoice;
    pdfStatus?: FinancePdfStatus;
    auditEvents: DocumentAuditEvent[] = [];
    complianceEvents: AccountingComplianceEvent[] = [];
    glPreview?: GlPreview;
    qrDialogVisible = false;
    activeTab = 'overview';
    workflowLoadingAction: AccountingDocumentAction | null = null;
    markingSent = false;
    retryingZatca = false;
    generatingReceipt = false;
    commercialDialogVisible = false;
    commercialSaving = false;
    commercialExternalReference = '';
    commercialDueDate = '';
    originalCommercialDueDate = '';
    commercialProjectId: number | null = null;
    commercialContactId: number | null = null;
    commercialContacts: CrmClientCommunication[] = [];
    commercialContactsLoading = false;
    commercialError = '';
    private commercialContactLoad = 0;
    commercialLineComments: Array<{ lineId: number; description: string; comment: string }> = [];

    constructor(private route: ActivatedRoute, private router: Router, private accountsService: AccountsService, private messageService: MessageService, public crmPartyContext: CrmPartyContextService, @Optional() public permissionService?: PermissionService) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.load();
    }

    onTabChange(value: string | number) {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'lines', 'receipts', 'debitnotes', 'journal', 'attachments', 'compliance', 'workflow', 'activity', 'related'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    compactReference(value?: string): string {
        if (!value) return '—';
        const operationalNumber = value.match(/(?:SHP|DLR|SO|PO|CLR)\/\d{4}\/\d{2}\/\d+/)?.[0];
        return operationalNumber || (value.length > 44 ? `${value.slice(0, 41)}…` : value);
    }

    load() {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.pdfStatus = undefined;
        this.accountsService.getInvoiceById(id).subscribe(invoice => {
            this.invoice = invoice;
            this.accountsService.invoiceDocumentStatus(id).subscribe({
                next: status => this.pdfStatus = status,
                error: () => this.pdfStatus = { status: 'MISSING', reprintAvailable: false }
            });
            this.accountsService.getAuditEvents('INVOICE', id).subscribe(events => this.auditEvents = events);
            this.accountsService.getComplianceEvents('INVOICE', id).subscribe(events => this.complianceEvents = events);
            this.accountsService.getGlPreview('INVOICE', id).subscribe(preview => this.glPreview = preview);
        });
    }

    workflow(action: AccountingDocumentAction, reason?: string) {
        if (!this.invoice?.invoiceId) return;
        if (this.invoiceWasPosted && action !== 'close') {
            this.messageService.add({ severity: 'warn', summary: 'Adjustment note required', detail: 'Correct a posted customer invoice through a linked credit or debit note.' });
            return;
        }
        this.workflowLoadingAction = action;
        this.accountsService.runInvoiceWorkflow(this.invoice.invoiceId, action, { source: 'PORTAL', reason }).pipe(
            finalize(() => this.workflowLoadingAction = null)
        ).subscribe({
            next: () => this.load(),
            error: err => this.messageService.add({ severity: 'error', summary: 'Workflow Failed', detail: err?.error?.message || 'Could not update invoice workflow.' })
        });
    }

    post() {
        if (!this.invoice?.invoiceId) return;
        this.workflowLoadingAction = 'post';
        this.accountsService.postInvoice(this.invoice.invoiceId, { source: 'PORTAL', comment: 'Posted from invoice detail' }).pipe(
            finalize(() => this.workflowLoadingAction = null)
        ).subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Posted', detail: 'Invoice posted successfully.' }); this.load(); },
            error: err => this.messageService.add({ severity: 'error', summary: 'Post Failed', detail: err.error?.message || 'Could not post invoice.' })
        });
    }

    retryZatca() {
        if (!this.invoice?.invoiceId) return;
        this.retryingZatca = true;
        this.accountsService.retryZatcaIntegration(this.invoice.invoiceId).pipe(
            finalize(() => this.retryingZatca = false)
        ).subscribe({
            next: () => this.load(),
            error: err => this.messageService.add({ severity: 'error', summary: 'Retry Failed', detail: err?.error?.message || 'Could not retry ZATCA.' })
        });
    }

    generateReceipt() {
        if (!this.invoice) return;
        this.generatingReceipt = true;
        this.accountsService.generateReceiptFromInvoices([this.invoice]).pipe(
            finalize(() => this.generatingReceipt = false)
        ).subscribe({
            next: receipt => {
                this.messageService.add({ severity: 'success', summary: 'Receipt Created', detail: `Receipt #${receipt.receiptNumber} generated.` });
                this.load();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Receipt Failed', detail: err.error?.message || 'Could not generate receipt.' })
        });
    }

    get invoiceWasPosted(): boolean {
        return !!this.invoice && (this.invoice.postingStatus === 'POSTED' || this.invoice.documentStatus === 'POSTED' || this.invoice.postedAt != null);
    }

    createDebitNote() {
        if (!this.invoice?.invoiceId || this.invoice.postingStatus !== 'POSTED'
            || !this.permissionService?.getPermissionCodes().has('accounts.debit-note.create')) return;
        this.router.navigate(['/accounts/enterprise/receivables/debit-note/add'], {
            queryParams: {
                originalInvoiceId: this.invoice.invoiceId,
                originalInvoiceNumber: this.invoice.invoiceNumber
            }
        });
    }

    createCustomerCreditNote() {
        if (!this.invoice?.invoiceId || this.invoice.postingStatus !== 'POSTED'
            || !this.permissionService?.getPermissionCodes().has('accounts.customer-credit-note.create')) return;
        this.router.navigate(['/accounts/enterprise/receivables/customer-credit-note/add'], {
            queryParams: { originalInvoiceId: this.invoice.invoiceId }
        });
    }

    markSent() {
        if (!this.invoice?.invoiceId) return;
        this.markingSent = true;
        this.accountsService.markInvoiceSent(this.invoice.invoiceId, this.invoice.deliveryReference).pipe(
            finalize(() => this.markingSent = false)
        ).subscribe({
            next: invoice => {
                this.invoice = invoice;
                this.messageService.add({ severity: 'success', summary: invoice.sentStatus || 'SENT', detail: `Invoice ${invoice.invoiceNumber || invoice.invoiceId} delivery status updated.` });
                this.load();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Send update failed', detail: err?.error?.message || 'Could not update invoice delivery status.' })
        });
    }

    showQr() {
        if (!this.invoice?.qrCodeValue) {
            this.messageService.add({ severity: 'warn', summary: 'QR unavailable', detail: 'This invoice does not have a QR payload.' });
            return;
        }
        this.qrDialogVisible = true;
    }

    copyQr() {
        if (!this.invoice?.qrCodeValue) return;
        navigator.clipboard?.writeText(this.invoice.qrCodeValue);
        this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'QR payload copied to clipboard.' });
    }

    downloadXml() {
        const xmlUrl = (this.invoice as any)?.xmlFileUrl || (this.invoice as any)?.zatcaXmlUrl;
        if (!xmlUrl) {
            this.messageService.add({ severity: 'warn', summary: 'XML unavailable', detail: 'No invoice XML URL is exposed by the backend for this invoice.' });
            return;
        }
        window.open(xmlUrl, '_blank');
    }

    download() {
        if (!this.invoice?.invoiceId || !this.documentActions.begin('download')) return;
        if (this.pdfStatus?.status === 'MISSING') {
            this.documentActions.end('download');
            this.messageService.add({ severity: 'warn', summary: 'Original unavailable', detail: 'No stored issued PDF is available for this invoice.' });
            return;
        }
        this.accountsService.downloadInvoiceDocument(this.invoice.invoiceId).pipe(
            finalize(() => this.documentActions.end('download'))
        ).subscribe({
            next: pdf => this.accountsService.downloadPdfDocument(pdf, 'invoice', this.invoice?.invoiceNumber, this.invoice?.invoiceId, this.invoice?.postingStatus),
            error: err => this.messageService.add({ severity: 'error', summary: 'Document unavailable', detail: err?.error?.message || 'The invoice PDF is unavailable.' })
        });
    }

    reprint() {
        if (!this.invoice?.invoiceId || !this.pdfStatus?.reprintAvailable || !this.documentActions.begin('reprint')) return;
        this.accountsService.reprintInvoiceDocument(this.invoice.invoiceId).pipe(
            finalize(() => this.documentActions.end('reprint'))
        ).subscribe({
            next: pdf => this.accountsService.downloadPdfDocument(pdf, 'invoice', this.invoice?.invoiceNumber, this.invoice?.invoiceId, 'REPRINT'),
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Reprint unavailable', detail: err?.status === 409 ? 'Commercial details no longer differ from the issued PDF.' : 'Could not reprint the invoice.' });
                this.load();
            }
        });
    }

    back() {
        this.router.navigate(['/accounts/enterprise/receivables/generated-invoices']);
    }

    canEditInvoice(): boolean {
        const documentStatus = this.invoice?.documentStatus;
        const postingStatus = this.invoice?.postingStatus;
        return !!this.invoice?.invoiceId
            && isEditableFinancialDraft(this.invoice);
    }

    editInvoice() {
        if (!this.invoice?.invoiceId || !this.canEditInvoice()) return;
        this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/manual-draft', this.invoice.invoiceId]);
    }

    canEditCommercialDetails(): boolean {
        const documentStatus = this.invoice?.documentStatus || '';
        return !!this.invoice?.invoiceId
            && this.invoice?.postingStatus === 'POSTED'
            && !['CANCELLED', 'VOIDED', 'REVERSED', 'CLOSED'].includes(documentStatus);
    }

    openCommercialDetails() {
        if (!this.invoice || !this.canEditCommercialDetails()) return;
        this.commercialExternalReference = this.invoice.externalReferenceNumber || '';
        this.commercialDueDate = this.invoice.dueDate ? formatDate(this.invoice.dueDate, 'yyyy-MM-dd', 'en-US') : '';
        this.originalCommercialDueDate = this.commercialDueDate;
        this.commercialProjectId = this.invoice.logisticsProjectId ?? null;
        this.commercialContactId = this.invoice.crmClientContactId ?? null;
        this.commercialError = '';
        this.commercialContacts = [];
        const load = ++this.commercialContactLoad;
        this.commercialContactsLoading = !!this.invoice.crmClientId;
        if (this.invoice.crmClientId) this.crmPartyContext.loadContactsStrict(this.invoice.crmClientId, this.invoice.owningCompany?.id).subscribe({
            next: contacts => {
                if (load !== this.commercialContactLoad) return;
                this.commercialContacts = contacts;
                this.commercialContactsLoading = false;
            },
            error: () => {
                if (load !== this.commercialContactLoad) return;
                this.commercialContactsLoading = false;
                this.commercialError = 'commercialContextEdit.contactsLoadFailed';
            }
        });
        this.commercialLineComments = (this.invoice.invoiceLines || [])
            .filter(line => line.invoiceLineId != null)
            .map(line => ({
                lineId: line.invoiceLineId!,
                description: line.itemDescription || line.itemNumber || `Line ${line.invoiceLineId}`,
                comment: line.invoiceComment || ''
            }));
        this.commercialDialogVisible = true;
    }

    saveCommercialDetails() {
        if (!this.invoice?.invoiceId || this.invoice.rowVersion == null || this.commercialSaving) return;
        if (this.originalCommercialDueDate && !this.commercialDueDate) {
            this.commercialError = 'commercialContextEdit.dueDateRequired';
            this.messageService.add({ severity: 'warn', summary: 'Due date required', detail: 'Select a due date or cancel the edit.' });
            return;
        }
        this.commercialSaving = true;
        this.commercialError = '';
        this.accountsService.updateInvoiceCommercialAnnotations(this.invoice.invoiceId, {
            rowVersion: this.invoice.rowVersion,
            externalReferenceNumber: this.commercialExternalReference.trim() || null,
            dueDate: this.commercialDueDate !== this.originalCommercialDueDate ? this.commercialDueDate : undefined,
            logisticsProjectId: this.commercialProjectId,
            crmClientContactId: this.commercialContactId,
            lineComments: this.commercialLineComments.map(line => ({
                lineId: line.lineId,
                comment: line.comment.trim() || null
            }))
        }).pipe(finalize(() => this.commercialSaving = false)).subscribe({
            next: invoice => {
                this.invoice = invoice;
                this.commercialDialogVisible = false;
                this.messageService.add({ severity: 'success', summary: 'Commercial details saved', detail: 'The revised due date and annotations appear on eligible commercial reprints; the issued PDF stays unchanged.' });
                this.load();
            },
            error: err => {
                if (err?.status === 409) {
                    this.commercialDialogVisible = false;
                    this.messageService.add({ severity: 'warn', summary: 'Draft changed elsewhere', detail: 'The invoice was reloaded. Review the latest version before saving again.' });
                    this.load();
                    return;
                }
                this.commercialError = err?.error?.message || 'Could not update the posted invoice annotations.';
                this.messageService.add({ severity: 'error', summary: 'Commercial details not saved', detail: err?.error?.message || 'Could not update the posted invoice annotations.' });
            }
        });
    }

    isActionBlocked(currentActionLoading: boolean): boolean {
        return this.hasPendingMutation() && !currentActionLoading;
    }

    private hasPendingMutation(): boolean {
        return !!this.workflowLoadingAction || this.markingSent || this.retryingZatca || this.generatingReceipt || this.commercialSaving;
    }

    formatMoney(amount?: number, currencyCode = '') {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: currencyFractionDigits(currencyCode || 'SAR'), maximumFractionDigits: currencyFractionDigits(currencyCode || 'SAR') })}`;
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'POSTED' || status === 'APPROVED') return 'success';
        if (status === 'DRAFT' || status === 'SUBMITTED') return 'info';
        if (status === 'REVERSED' || status === 'VOIDED') return 'danger';
        return 'secondary';
    }
}
